/**
 * BprFrameworkPage - /so/bpr/framework
 *
 * Framework administration for the BPR knowledge store:
 * - Versions: list with status chips, triangle viewer with citations +
 *   confidence per node, approve/reject (workspace admin), feedback form,
 *   and the "Generate new version" dispatch.
 * - Sources: register (incl. pasted contentText + trust weight), edit, retire.
 * - Overrides: list, create (scope/targetPath/operation/payload JSON), retire.
 */

import type {
  BprFeedbackAction,
  BprFrameworkVersionDetail,
  BprFrameworkVersionSummary,
  BprOverride,
  BprOverrideOperation,
  BprOverrideScope,
  BprSource,
  BprSourceType,
} from '@protoss/shared';
import {
  BPR_FEEDBACK_ACTIONS,
  BPR_OVERRIDE_OPERATIONS,
  BPR_OVERRIDE_SCOPES,
  BPR_SOURCE_TYPES,
} from '@protoss/shared';
import type { PillVariant } from '@protoss/ui';
import {
  Button,
  Pill,
  Select,
  SelectItem,
  SliderField,
  Tabs,
  TextArea,
  TextField,
} from '@protoss/ui';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { AppNav } from '../components/AppNav';
import { PageErrorState } from '../components/shared/PageErrorState';
import { AppShell, MainContentShell, type RightRailItem, useModuleChatRailItem } from '../components/shell';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { BprFrameworkTriangle } from '../features/bpr/components/BprFrameworkTriangle';
import { bprApi } from '../features/bpr/services/bprApi';
import { useWorkspaceAdmin } from '../hooks/useWorkspaceAdmin';
import './bpr-page.css';
import './bpr-framework-page.css';

function versionStatusPresentation(status: string): { label: string; variant: PillVariant } {
  switch (status) {
    case 'draft':
      return { label: 'Draft', variant: 'warning' };
    case 'approved':
      return { label: 'Approved', variant: 'success' };
    case 'archived':
      return { label: 'Archived', variant: 'muted' };
    case 'fallback':
      return { label: 'Fallback', variant: 'ghost' };
    default:
      return { label: status, variant: 'neutral' };
  }
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ─── Versions tab ────────────────────────────────────────────────────────────

function FeedbackForm({ versionUuid }: { versionUuid: string }) {
  const [action, setAction] = React.useState<BprFeedbackAction>('comment');
  const [targetPath, setTargetPath] = React.useState('');
  const [rating, setRating] = React.useState<number | null>(null);
  const [comment, setComment] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);

  const handleSubmit = async () => {
    if (isSaving) return;
    setIsSaving(true);
    setMessage(null);
    try {
      await bprApi.recordFrameworkFeedback(versionUuid, {
        action,
        ...(targetPath.trim() !== '' ? { targetPath: targetPath.trim() } : {}),
        ...(action === 'rate' && rating != null ? { rating } : {}),
        ...(comment.trim() !== '' ? { comment: comment.trim() } : {}),
      });
      setComment('');
      setTargetPath('');
      setMessage('Feedback recorded.');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Failed to record feedback.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bpr-framework__feedback">
      <h4 className="bpr-section__subheading">Feedback</h4>
      <div className="bpr-framework__feedback-row">
        <label className="bpr-dialog__field">
          <span className="bpr-dialog__field-label">Action</span>
          <Select value={action} onValueChange={(v) => setAction(v as BprFeedbackAction)} size="sm">
            {BPR_FEEDBACK_ACTIONS.map((a) => (
              <SelectItem key={a} value={a}>
                {a}
              </SelectItem>
            ))}
          </Select>
        </label>
        <TextField
          label="Target path"
          value={targetPath}
          onChange={(e) => setTargetPath(e.target.value)}
          placeholder="areaKey.issueKey.patternKey (optional)"
        />
      </div>
      {action === 'rate' ? (
        <SliderField
          label="Rating"
          value={rating}
          onChange={setRating}
          min={1}
          max={5}
          step={1}
          defaultValue={3}
        />
      ) : null}
      <TextArea
        label="Comment"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={3}
        placeholder="What is right or wrong about this node / version?"
      />
      <div className="bpr-framework__feedback-actions">
        <Button type="button" size="sm" disabled={isSaving} onClick={() => void handleSubmit()}>
          {isSaving ? 'Recording…' : 'Record feedback'}
        </Button>
        {message ? <span className="bpr-framework__feedback-message">{message}</span> : null}
      </div>
    </div>
  );
}

function VersionsTab({ isWorkspaceAdmin }: { isWorkspaceAdmin: boolean }) {
  const [versions, setVersions] = React.useState<BprFrameworkVersionSummary[]>([]);
  const [detail, setDetail] = React.useState<BprFrameworkVersionDetail | null>(null);
  const [selectedUuid, setSelectedUuid] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [isActing, setIsActing] = React.useState(false);
  const [rejectComment, setRejectComment] = React.useState('');
  const [generateToolKey, setGenerateToolKey] = React.useState('');
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [generateMessage, setGenerateMessage] = React.useState<string | null>(null);

  const loadVersions = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setVersions(await bprApi.listFrameworkVersions());
    } catch {
      setError('Failed to load framework versions.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadVersions();
  }, [loadVersions]);

  React.useEffect(() => {
    if (!selectedUuid) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetail(null);
    void bprApi
      .getFrameworkVersion(selectedUuid)
      .then((loaded) => {
        if (!cancelled) setDetail(loaded);
      })
      .catch(() => {
        if (!cancelled) setActionError('Failed to load the version detail.');
      });
    return () => {
      cancelled = true;
    };
  }, [selectedUuid]);

  const handleApprove = async () => {
    if (!detail || isActing) return;
    setIsActing(true);
    setActionError(null);
    try {
      await bprApi.approveFrameworkVersion(detail.uuid);
      await loadVersions();
      setSelectedUuid(detail.uuid);
      setDetail(await bprApi.getFrameworkVersion(detail.uuid));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to approve the version.');
    } finally {
      setIsActing(false);
    }
  };

  const handleReject = async () => {
    if (!detail || isActing) return;
    setIsActing(true);
    setActionError(null);
    try {
      await bprApi.rejectFrameworkVersion(detail.uuid, rejectComment.trim() || undefined);
      setRejectComment('');
      await loadVersions();
      setSelectedUuid(detail.uuid);
      setDetail(await bprApi.getFrameworkVersion(detail.uuid));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to reject the version.');
    } finally {
      setIsActing(false);
    }
  };

  const handleGenerate = async () => {
    if (generateToolKey.trim() === '' || isGenerating) return;
    setIsGenerating(true);
    setGenerateMessage(null);
    try {
      await bprApi.generateFramework({ toolKey: generateToolKey.trim() });
      setGenerateMessage(
        'Generation dispatched. A new draft version will appear once the run completes.'
      );
    } catch (err) {
      setGenerateMessage(err instanceof Error ? err.message : 'Failed to dispatch generation.');
    } finally {
      setIsGenerating(false);
    }
  };

  if (error) return <PageErrorState message={error} onRetry={() => void loadVersions()} />;

  return (
    <div className="bpr-framework__versions">
      {isWorkspaceAdmin ? (
        <div className="bpr-framework__generate">
          <TextField
            label="Tool key"
            value={generateToolKey}
            onChange={(e) => setGenerateToolKey(e.target.value)}
            placeholder="e.g. oracle:netsuite"
          />
          <Button
            type="button"
            size="sm"
            disabled={generateToolKey.trim() === '' || isGenerating}
            onClick={() => void handleGenerate()}
          >
            {isGenerating ? 'Dispatching…' : 'Generate new version'}
          </Button>
          {generateMessage ? (
            <span className="bpr-framework__feedback-message">{generateMessage}</span>
          ) : null}
        </div>
      ) : null}

      <div className="bpr-framework__versions-layout">
        <ul className="bpr-framework__version-list">
          {loading ? <li className="bpr-section__empty">Loading versions…</li> : null}
          {!loading && versions.length === 0 ? (
            <li className="bpr-section__empty">No framework versions yet.</li>
          ) : null}
          {versions.map((version) => {
            const { label, variant } = versionStatusPresentation(version.status);
            return (
              <li key={version.uuid}>
                <button
                  type="button"
                  className={`bpr-framework__version-item${
                    selectedUuid === version.uuid ? ' bpr-framework__version-item--selected' : ''
                  }`}
                  onClick={() => setSelectedUuid(version.uuid)}
                >
                  <span className="bpr-framework__version-tool">{version.toolKey}</span>
                  <span className="bpr-framework__version-meta">
                    {version.scope} · {formatDateTime(version.generatedAt ?? version.createdAt)}
                    {version.stats
                      ? ` · ${version.stats.areas}a/${version.stats.issues}i/${version.stats.patterns}p`
                      : ''}
                  </span>
                  <Pill variant={variant}>{label}</Pill>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="bpr-framework__version-detail">
          {actionError ? <p className="bpr-dialog__error">{actionError}</p> : null}
          {!selectedUuid ? (
            <p className="bpr-section__empty">Select a version to review it.</p>
          ) : !detail ? (
            <p className="bpr-section__empty">Loading version…</p>
          ) : (
            <>
              <div className="bpr-framework__version-actions">
                <Pill variant={versionStatusPresentation(detail.status).variant}>
                  {versionStatusPresentation(detail.status).label}
                </Pill>
                {detail.confidence != null ? (
                  <span className="bpr-framework__version-confidence">
                    Overall confidence {Math.round(detail.confidence * 100)}%
                  </span>
                ) : null}
                {isWorkspaceAdmin && detail.status === 'draft' ? (
                  <>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={isActing}
                      onClick={() => void handleApprove()}
                    >
                      Approve
                    </Button>
                    <TextField
                      value={rejectComment}
                      onChange={(e) => setRejectComment(e.target.value)}
                      placeholder="Rejection comment (optional)"
                      aria-label="Rejection comment"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      disabled={isActing}
                      onClick={() => void handleReject()}
                    >
                      Reject
                    </Button>
                  </>
                ) : null}
              </div>
              {detail.sourceManifest && detail.sourceManifest.length > 0 ? (
                <details className="bpr-framework__manifest">
                  <summary>Source manifest ({detail.sourceManifest.length})</summary>
                  <ul>
                    {detail.sourceManifest.map((entry, idx) => (
                      <li key={idx}>
                        {entry.title ?? entry.sourceUuid ?? entry.documentUuid ?? 'Source'}
                        {entry.sourceType ? ` (${entry.sourceType})` : ''}
                        {entry.trustWeight != null ? ` · trust ${entry.trustWeight}` : ''}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
              <BprFrameworkTriangle content={detail.content} showCitations />
              <FeedbackForm versionUuid={detail.uuid} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sources tab ─────────────────────────────────────────────────────────────

function SourcesTab() {
  const [sources, setSources] = React.useState<BprSource[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [toolKey, setToolKey] = React.useState('');
  const [sourceType, setSourceType] = React.useState<BprSourceType>('other');
  const [title, setTitle] = React.useState('');
  const [uri, setUri] = React.useState('');
  const [trustWeight, setTrustWeight] = React.useState<number | null>(null);
  const [contentText, setContentText] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);
  const [retiringUuid, setRetiringUuid] = React.useState<string | null>(null);

  const loadSources = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSources(await bprApi.listSources());
    } catch {
      setError('Failed to load sources.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadSources();
  }, [loadSources]);

  const canRegister = toolKey.trim() !== '' && title.trim() !== '' && !isSaving;

  const handleRegister = async () => {
    if (!canRegister) return;
    setIsSaving(true);
    setFormError(null);
    try {
      await bprApi.registerSource({
        toolKey: toolKey.trim(),
        sourceType,
        title: title.trim(),
        ...(uri.trim() !== '' ? { uri: uri.trim() } : {}),
        ...(trustWeight != null ? { trustWeight } : {}),
        ...(contentText.trim() !== '' ? { contentText: contentText.trim() } : {}),
      });
      setTitle('');
      setUri('');
      setContentText('');
      await loadSources();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to register the source.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRetire = async (source: BprSource) => {
    if (retiringUuid) return;
    setRetiringUuid(source.uuid);
    try {
      await bprApi.updateSource(source.uuid, { status: 'retired' });
      await loadSources();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to retire the source.');
    } finally {
      setRetiringUuid(null);
    }
  };

  if (error) return <PageErrorState message={error} onRetry={() => void loadSources()} />;

  return (
    <div className="bpr-framework__sources">
      <div className="bpr-framework__form-card">
        <h3 className="bpr-section__subheading">Register source</h3>
        <div className="bpr-framework__form-grid">
          <TextField
            label="Tool key"
            value={toolKey}
            onChange={(e) => setToolKey(e.target.value)}
            placeholder="e.g. oracle:netsuite"
            required
          />
          <label className="bpr-dialog__field">
            <span className="bpr-dialog__field-label">Source type</span>
            <Select value={sourceType} onValueChange={(v) => setSourceType(v as BprSourceType)}>
              {BPR_SOURCE_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </Select>
          </label>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
          <TextField
            label="URI"
            value={uri}
            onChange={(e) => setUri(e.target.value)}
            placeholder="https://… (optional)"
          />
        </div>
        <SliderField
          label="Trust weight"
          value={trustWeight}
          onChange={setTrustWeight}
          min={0}
          max={1}
          step={0.05}
          defaultValue={0.5}
          minLabel="0 (untrusted)"
          maxLabel="1 (authoritative)"
        />
        <TextArea
          label="Content"
          value={contentText}
          onChange={(e) => setContentText(e.target.value)}
          rows={6}
          placeholder="Paste the source content here; it is ingested immediately."
        />
        {formError ? <p className="bpr-dialog__error">{formError}</p> : null}
        <div className="bpr-framework__form-actions">
          <Button type="button" size="sm" disabled={!canRegister} onClick={() => void handleRegister()}>
            {isSaving ? 'Registering…' : 'Register source'}
          </Button>
        </div>
      </div>

      <div className="bpr-framework__list">
        <h3 className="bpr-section__subheading">Sources ({sources.length})</h3>
        {loading ? <p className="bpr-section__empty">Loading sources…</p> : null}
        {!loading && sources.length === 0 ? (
          <p className="bpr-section__empty">No sources registered yet.</p>
        ) : null}
        <ul className="bpr-framework__rows">
          {sources.map((source) => (
            <li key={source.uuid} className="bpr-framework__row">
              <div className="bpr-framework__row-main">
                <span className="bpr-framework__row-title">{source.title}</span>
                <span className="bpr-framework__row-meta">
                  {source.toolKey} · {source.sourceType} · trust {source.trustWeight}
                  {source.lastIngestedAt
                    ? ` · ingested ${formatDateTime(source.lastIngestedAt)}`
                    : ''}
                </span>
              </div>
              <Pill variant={source.status === 'active' ? 'success' : 'muted'}>
                {source.status}
              </Pill>
              {source.status === 'active' ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={retiringUuid !== null}
                  onClick={() => void handleRetire(source)}
                >
                  {retiringUuid === source.uuid ? 'Retiring…' : 'Retire'}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ─── Overrides tab ───────────────────────────────────────────────────────────

function OverridesTab() {
  const [overrides, setOverrides] = React.useState<BprOverride[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [scope, setScope] = React.useState<BprOverrideScope>('org');
  const [toolKey, setToolKey] = React.useState('');
  const [targetPath, setTargetPath] = React.useState('');
  const [operation, setOperation] = React.useState<BprOverrideOperation>('modify');
  const [payloadJson, setPayloadJson] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);
  const [retiringUuid, setRetiringUuid] = React.useState<string | null>(null);

  const loadOverrides = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setOverrides(await bprApi.listOverrides());
    } catch {
      setError('Failed to load overrides.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadOverrides();
  }, [loadOverrides]);

  const canCreate = toolKey.trim() !== '' && targetPath.trim() !== '' && !isSaving;

  const handleCreate = async () => {
    if (!canCreate) return;
    let payload: unknown;
    if (payloadJson.trim() !== '') {
      try {
        payload = JSON.parse(payloadJson);
      } catch {
        setFormError('Payload must be valid JSON.');
        return;
      }
    }
    setIsSaving(true);
    setFormError(null);
    try {
      await bprApi.createOverride({
        scope,
        toolKey: toolKey.trim(),
        targetPath: targetPath.trim(),
        operation,
        ...(payload !== undefined
          ? { payload: payload as { node?: unknown; rank?: number; reason?: string } }
          : {}),
      });
      setTargetPath('');
      setPayloadJson('');
      await loadOverrides();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create the override.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRetire = async (override: BprOverride) => {
    if (retiringUuid) return;
    setRetiringUuid(override.uuid);
    try {
      await bprApi.retireOverride(override.uuid);
      await loadOverrides();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to retire the override.');
    } finally {
      setRetiringUuid(null);
    }
  };

  if (error) return <PageErrorState message={error} onRetry={() => void loadOverrides()} />;

  return (
    <div className="bpr-framework__overrides">
      <div className="bpr-framework__form-card">
        <h3 className="bpr-section__subheading">Create override</h3>
        <div className="bpr-framework__form-grid">
          <label className="bpr-dialog__field">
            <span className="bpr-dialog__field-label">Scope</span>
            <Select value={scope} onValueChange={(v) => setScope(v as BprOverrideScope)}>
              {BPR_OVERRIDE_SCOPES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </Select>
          </label>
          <label className="bpr-dialog__field">
            <span className="bpr-dialog__field-label">Operation</span>
            <Select
              value={operation}
              onValueChange={(v) => setOperation(v as BprOverrideOperation)}
            >
              {BPR_OVERRIDE_OPERATIONS.map((op) => (
                <SelectItem key={op} value={op}>
                  {op}
                </SelectItem>
              ))}
            </Select>
          </label>
          <TextField
            label="Tool key"
            value={toolKey}
            onChange={(e) => setToolKey(e.target.value)}
            placeholder="e.g. oracle:netsuite"
            required
          />
          <TextField
            label="Target path"
            value={targetPath}
            onChange={(e) => setTargetPath(e.target.value)}
            placeholder="areaKey.issueKey.patternKey"
            required
          />
        </div>
        <TextArea
          label="Payload (JSON)"
          value={payloadJson}
          onChange={(e) => setPayloadJson(e.target.value)}
          rows={5}
          placeholder='{"rank": 1} or {"node": {…}} or {"reason": "…"}'
        />
        {formError ? <p className="bpr-dialog__error">{formError}</p> : null}
        <div className="bpr-framework__form-actions">
          <Button type="button" size="sm" disabled={!canCreate} onClick={() => void handleCreate()}>
            {isSaving ? 'Creating…' : 'Create override'}
          </Button>
        </div>
      </div>

      <div className="bpr-framework__list">
        <h3 className="bpr-section__subheading">Overrides ({overrides.length})</h3>
        {loading ? <p className="bpr-section__empty">Loading overrides…</p> : null}
        {!loading && overrides.length === 0 ? (
          <p className="bpr-section__empty">No overrides yet.</p>
        ) : null}
        <ul className="bpr-framework__rows">
          {overrides.map((override) => (
            <li key={override.uuid} className="bpr-framework__row">
              <div className="bpr-framework__row-main">
                <span className="bpr-framework__row-title">
                  {override.operation} · {override.targetPath}
                </span>
                <span className="bpr-framework__row-meta">
                  {override.toolKey} · scope {override.scope}
                </span>
              </div>
              <Pill variant={override.status === 'active' ? 'success' : 'muted'}>
                {override.status}
              </Pill>
              {override.status === 'active' ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={retiringUuid !== null}
                  onClick={() => void handleRetire(override)}
                >
                  {retiringUuid === override.uuid ? 'Retiring…' : 'Retire'}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

type FrameworkTab = 'versions' | 'sources' | 'overrides';

export function BprFrameworkPage() {
  const navigate = useNavigate();
  const { workspace, isLoadingWorkspace } = useWorkspace();
  const isWorkspaceAdmin = useWorkspaceAdmin(workspace);
  const moduleChatItem = useModuleChatRailItem();
  const [tab, setTab] = React.useState<FrameworkTab>('versions');

  if (isLoadingWorkspace) return null;

  const leftNav = (
    <AppNav
      selected="bpr"
      hidePrimary
      isWorkspaceAdmin={isWorkspaceAdmin}
      onAdministrationClick={() => navigate('/administration')}
    />
  );

  const rightRailItems: RightRailItem[] = moduleChatItem ? [moduleChatItem] : [];

  const main = (
    <MainContentShell
      title="BPR framework"
      breadcrumb="Business Process Reviews > Framework"
      actions={
        <Button type="button" variant="outline" size="sm" onClick={() => navigate('/so/bpr')}>
          Engagements
        </Button>
      }
      subnav={
        <Tabs
          items={[
            { value: 'versions', label: 'Versions' },
            { value: 'sources', label: 'Sources' },
            { value: 'overrides', label: 'Overrides' },
          ]}
          value={tab}
          onValueChange={(value) => setTab(value as FrameworkTab)}
          ariaLabel="Framework administration sections"
        />
      }
    >
      <div className="bpr-framework__body">
        {tab === 'versions' ? <VersionsTab isWorkspaceAdmin={isWorkspaceAdmin} /> : null}
        {tab === 'sources' ? <SourcesTab /> : null}
        {tab === 'overrides' ? <OverridesTab /> : null}
      </div>
    </MainContentShell>
  );

  return <AppShell leftNav={leftNav} main={main} rightRailItems={rightRailItems} />;
}
